import { Skeleton } from '@/components/ui/skeleton';

/**
 * Skeleton com a forma do conteúdo (`spec/17` §7 item 6): uma linha de cabeçalho e linhas
 * de tabela com avatar, dois textos e as colunas à direita, na altura da linha real.
 */
export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  const rowIds = Array.from({ length: rows }, (_, index) => `row-${index}`);
  const columnIds = Array.from({ length: Math.max(1, columns - 2) }, (_, index) => `col-${index}`);
  return (
    <div role="status" aria-busy="true" aria-label="Carregando">
      <div className="flex h-10 items-center border-border border-b px-5">
        <Skeleton className="h-2.5 w-24" />
      </div>
      {rowIds.map((rowId) => (
        <div
          key={rowId}
          className="flex h-[57px] items-center gap-3 border-border border-b px-5 last:border-b-0"
        >
          <Skeleton className="size-8 shrink-0 rounded-pill" />
          <div className="grid min-w-0 flex-1 gap-1.5">
            <Skeleton className="h-3 w-36 max-w-full" />
            <Skeleton className="h-2.5 w-48 max-w-full" />
          </div>
          {columnIds.map((columnId) => (
            <Skeleton key={columnId} className="hidden h-3 w-16 lg:block" />
          ))}
          <Skeleton className="h-5 w-20 rounded-pill" />
        </div>
      ))}
    </div>
  );
}

/** Alturas (em %) das barras do esqueleto de gráfico: irregulares, como uma série de verdade. */
const BARS = [34, 62, 48, 20, 55, 90, 66, 40, 72, 30, 58, 44, 80, 36, 52, 26, 64, 46] as const;

/**
 * Esqueleto de gráfico de barras: barras de alturas variadas sobre a linha de base, o
 * formato do `VolumeCard`. Sem `role` próprio: quem chama já anuncia "Carregando".
 */
export function BarsSkeleton({ height = 150 }: { height?: number }) {
  return (
    <div
      aria-hidden="true"
      className="flex items-end gap-[3px] border-border border-b sm:gap-1.5"
      style={{ height }}
    >
      {BARS.map((pct, index) => (
        <Skeleton
          // biome-ignore lint/suspicious/noArrayIndexKey: barras de placeholder não têm identidade
          key={index}
          className="min-w-0 flex-1 rounded-t-[2px] rounded-b-none"
          style={{ height: `${pct}%` }}
        />
      ))}
    </div>
  );
}

export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  const lineIds = Array.from({ length: lines }, (_, index) => `line-${index}`);
  return (
    <div
      className="flex flex-col gap-3 rounded-2xl bg-card p-5 shadow-(--shadow-card)"
      role="status"
      aria-busy="true"
      aria-label="Carregando"
    >
      <Skeleton className="h-3 w-24 rounded-control" />
      <Skeleton className="h-9 w-40 rounded-control" />
      {lineIds.map((lineId) => (
        <Skeleton key={lineId} className="h-3 w-full rounded-control" />
      ))}
    </div>
  );
}
